// components/RegisterAgentForm.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Box,
  Stack,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  Chip,
  CircularProgress,
} from "@mui/material";
import api from "../api/axios";
import { useSnackbar } from "notistack";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SaveIcon from "@mui/icons-material/Save";

const generatePassword = (length = 10) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#?!";
  let out = "";
  for (let i = 0; i < length; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
};

const strengthInfo = (pwd = "") => {
  const p = pwd || "";
  const score =
    (p.length >= 8 ? 1 : 0) +
    (/[A-Z]/.test(p) ? 1 : 0) +
    (/[a-z]/.test(p) ? 1 : 0) +
    (/[0-9]/.test(p) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(p) ? 1 : 0);

  if (!p) return { label: "—", color: "default" };
  if (score <= 2) return { label: "Faible", color: "error" };
  if (score === 3) return { label: "Moyen", color: "warning" };
  return { label: "Fort", color: "success" };
};

const RegisterAgentForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    location_id: "",
    agency_id: "",
  });

  const [locations, setLocations] = useState([]);
  const [agencies, setAgencies] = useState([]);

  const [loadingRefs, setLoadingRefs] = useState(true);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const pwdStrength = useMemo(() => strengthInfo(form.password), [form.password]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingRefs(true);
        const locRes = await api.get("/settings/locations");
        setLocations(Array.isArray(locRes.data) ? locRes.data : []);
      } catch (err) {
        enqueueSnackbar("Échec du chargement des localisations", { variant: "error" });
        setLocations([]);
      } finally {
        setLoadingRefs(false);
      }
    };

    fetchLocations();
  }, [enqueueSnackbar]);

  useEffect(() => {
    const fetchAgencies = async () => {
      if (!form.location_id) {
        setAgencies([]);
        return;
      }

      try {
        setLoadingAgencies(true);
        const res = await api.get(`/agencies?location_id=${form.location_id}`);
        setAgencies(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        enqueueSnackbar("Échec du chargement des agences", { variant: "error" });
        setAgencies([]);
      } finally {
        setLoadingAgencies(false);
      }
    };

    fetchAgencies();
  }, [form.location_id, enqueueSnackbar]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (name === "location_id") {
        return {
          ...prev,
          location_id: value,
          agency_id: "",
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleGeneratePassword = () => {
    const pwd = generatePassword(10);
    setForm((prev) => ({ ...prev, password: pwd, confirmPassword: pwd }));
    enqueueSnackbar("Mot de passe généré", { variant: "success" });
  };

  const handleCopyPassword = async () => {
    try {
      if (!form.password) {
        enqueueSnackbar("Aucun mot de passe à copier", { variant: "warning" });
        return;
      }
      await navigator.clipboard.writeText(form.password);
      enqueueSnackbar("Mot de passe copié", { variant: "success" });
    } catch {
      enqueueSnackbar("Impossible de copier (permission navigateur)", { variant: "error" });
    }
  };

  const handleSubmit = async () => {
    const { name, email, password, confirmPassword, location_id, agency_id } = form;

    if (!name || !email || !password || !location_id || !agency_id) {
      enqueueSnackbar("Veuillez remplir tous les champs obligatoires", { variant: "warning" });
      return;
    }

    if (password.length < 6) {
      enqueueSnackbar("Mot de passe trop court (min 6 caractères)", { variant: "warning" });
      return;
    }

    if (password !== confirmPassword) {
      enqueueSnackbar("Les mots de passe ne correspondent pas", { variant: "error" });
      return;
    }

    try {
      setSubmitting(true);

      await api.post("/admin/register-agent", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        location_id,
        agency_id,
      });

      enqueueSnackbar("Agent enregistré avec succès", { variant: "success" });

      setForm({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        location_id: "",
        agency_id: "",
      });

      setAgencies([]);
      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || "Échec de l'enregistrement de l'agent";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const locationLabel = (loc) => {
    const city = loc?.city ?? loc?.name ?? "";
    const country = loc?.country ?? "";
    return [city, country].filter(Boolean).join(", ") || `Location #${loc?.id ?? ""}`;
  };

  const agencyLabel = (agency) => {
    return agency?.name || `Agence #${agency?.id ?? ""}`;
  };

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 1.5, sm: 2 }, mb: 3 }}>
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <PersonAddAltIcon />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  Enregistrer un nouvel agent
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Créez un agent et associez-le à une localisation et à une agence.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                type="button"
                variant="outlined"
                startIcon={<AutorenewIcon />}
                onClick={handleGeneratePassword}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Générer
              </Button>
              <Button
                type="button"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyPassword}
                disabled={!form.password}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Copier
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ p: 2 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            L’agent sera rattaché à une agence précise. Cette agence servira ensuite pour les retraits, la caisse et les permissions.
          </Alert>
        </Box>

        <Divider />

        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>Informations</Typography>

                <Stack spacing={1.5}>
                  <TextField
                    label="Nom de l'agent *"
                    name="name"
                    fullWidth
                    value={form.name}
                    onChange={handleChange}
                  />

                  <TextField
                    label="Email *"
                    name="email"
                    type="email"
                    fullWidth
                    value={form.email}
                    onChange={handleChange}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Localisation *</InputLabel>
                    <Select
                      name="location_id"
                      value={form.location_id}
                      onChange={handleChange}
                      label="Localisation *"
                      disabled={loadingRefs}
                    >
                      <MenuItem value="">—</MenuItem>
                      {locations.map((loc) => (
                        <MenuItem key={loc.id} value={loc.id}>
                          {locationLabel(loc)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth disabled={!form.location_id || loadingAgencies}>
                    <InputLabel>Agence *</InputLabel>
                    <Select
                      name="agency_id"
                      value={form.agency_id}
                      onChange={handleChange}
                      label="Agence *"
                    >
                      <MenuItem value="">—</MenuItem>
                      {agencies.map((agency) => (
                        <MenuItem key={agency.id} value={agency.id}>
                          {agencyLabel(agency)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {loadingRefs && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Chargement des localisations…
                      </Typography>
                    </Stack>
                  )}

                  {loadingAgencies && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Chargement des agences…
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 900 }}>Sécurité</Typography>
                  <Chip
                    label={`Force: ${pwdStrength.label}`}
                    color={pwdStrength.color}
                    variant={pwdStrength.color === "default" ? "outlined" : "filled"}
                    size="small"
                    sx={{ fontWeight: 900, borderRadius: 2 }}
                  />
                </Stack>

                <Stack spacing={1.5}>
                  <TextField
                    label="Mot de passe *"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    value={form.password}
                    onChange={handleChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((p) => !p)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Confirmer le mot de passe *"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    fullWidth
                    value={form.confirmPassword}
                    onChange={handleChange}
                    error={!!form.confirmPassword && form.confirmPassword !== form.password}
                    helperText={
                      !!form.confirmPassword && form.confirmPassword !== form.password
                        ? "Les mots de passe ne correspondent pas"
                        : " "
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirmPassword((p) => !p)} edge="end">
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Astuce: cliquez sur <b>Générer</b> pour créer un mot de passe solide, puis <b>Copier</b>.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end">
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={submitting}
                  startIcon={<SaveIcon />}
                  sx={{ borderRadius: 2, fontWeight: 900, py: 1.2 }}
                >
                  {submitting ? "Enregistrement..." : "Enregistrer l'agent"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterAgentForm;